import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const BILL_CATS = ["Internet", "Electricity", "Water", "Subscription", "Rent", "Insurance", "Loan", "Other"];

const getFontSizeClass = (value, baseSize = "text-3xl") => {
  const len = value ? String(value).length : 0;
  if (baseSize === "text-3xl") {
    if (len > 16) return "text-base";
    if (len > 14) return "text-lg";
    if (len > 11) return "text-xl";
    if (len > 9) return "text-2xl";
    return "text-3xl";
  }
  if (len > 16) return "text-sm";
  if (len > 14) return "text-base";
  if (len > 11) return "text-lg";
  if (len > 9) return "text-xl";
  return "text-2xl";
};

const formatNumberWithCommas = (value) => {
  if (!value) return "";
  const clean = String(value).replace(/\D/g, "");
  return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const parseNumberFromCommas = (value) => {
  if (!value) return 0;
  return Number(String(value).replace(/,/g, ""));
};

export default function Billing() {
  const { data: bills } = useSWR("/bills", fetcher);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDay, setDueDay] = useState(1);
  const [category, setCategory] = useState("Internet");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const create = async () => {
    if (!name.trim() || !amount) return toast.error("Nama dan jumlah harus diisi");
    try {
      await api.post("/bills", {
        name: name.trim(),
        amount: parseNumberFromCommas(amount),
        due_day: Number(dueDay),
        category,
      });
      toast.success("Tagihan ditambahkan");
      mutate("/bills");
      setOpen(false); setName(""); setAmount(""); setDueDay(1);
    } catch { toast.error("Gagal"); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/bills/${id}/toggle`); mutate("/bills"); } catch {}
  };
  const del = async (id) => {
    try { await api.delete(`/bills/${id}`); mutate("/bills"); toast.success("Dihapus"); } catch {}
  };

  const handleConfirmDelete = () => {
    if (confirmId) {
      del(confirmId);
      setConfirmId(null);
    }
    setConfirmOpen(false);
  };

  const totalMonthly = (bills || []).reduce((s, b) => s + b.amount, 0);
  const paidMonthly = (bills || []).filter((b) => b.is_paid).reduce((s, b) => s + b.amount, 0);
  const today = new Date().getDate();

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <p className="label-caps">Recurring</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Billing & Bills</h1>
          <p className="text-sm text-muted-foreground mt-2">Lacak tagihan bulanan dan langganan.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="add-bill-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Tagihan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Tagihan Baru</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Nama</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11" placeholder="Netflix, PLN…" data-testid="bill-name-input" /></div>
              <div><Label>Jumlah (IDR)</Label><Input type="text" value={amount} onChange={(e) => setAmount(formatNumberWithCommas(e.target.value))} className="mt-2 h-11 font-mono" placeholder="0" data-testid="bill-amount-input" /></div>
              <div><Label>Jatuh Tempo (Tgl)</Label><Input type="number" min="1" max="31" value={dueDay} onChange={(e) => setDueDay(e.target.value)} className="mt-2 h-11" data-testid="bill-due-input" /></div>
              <div>
                <Label>Kategori</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-2 h-11" data-testid="bill-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILL_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-foreground text-background" data-testid="create-bill-submit">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <div className="border border-border bg-card p-5">
          <p className="label-caps">Total Bulanan</p>
          <p className={`metric-num mt-3 whitespace-nowrap truncate ${getFontSizeClass(formatIDR(totalMonthly), "text-3xl")}`}>{formatIDR(totalMonthly)}</p>
        </div>
        <div className="border border-border bg-card p-5">
          <p className="label-caps">Sudah Dibayar</p>
          <p className={`metric-num mt-3 text-gain whitespace-nowrap truncate ${getFontSizeClass(formatIDR(paidMonthly), "text-3xl")}`}>{formatIDR(paidMonthly)}</p>
        </div>
        <div className="border border-border bg-card p-5">
          <p className="label-caps">Belum Dibayar</p>
          <p className={`metric-num mt-3 text-loss whitespace-nowrap truncate ${getFontSizeClass(formatIDR(totalMonthly - paidMonthly), "text-3xl")}`}>{formatIDR(totalMonthly - paidMonthly)}</p>
        </div>
      </div>

      <div className="mt-8 border border-border bg-card">
        {(!bills || bills.length === 0) ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Belum ada tagihan.</div>
        ) : bills.map((b) => {
          const overdue = !b.is_paid && b.due_day < today;
          return (
            <div key={b.id} className="flex items-center gap-4 p-4 border-b border-border last:border-0 hover:bg-accent/40 transition-colors" data-testid={`bill-row-${b.id}`}>
              <button onClick={() => toggle(b.id)} data-testid={`bill-toggle-${b.id}`}>
                {b.is_paid ? <CheckCircle2 className="h-5 w-5 text-gain" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${b.is_paid ? "line-through text-muted-foreground" : ""}`}>{b.name}</p>
                <p className="text-xs text-muted-foreground">{b.category} • Jatuh tempo tgl {b.due_day}{overdue && <span className="text-loss ml-2">• TERLAMBAT</span>}</p>
              </div>
              <p className="font-mono text-sm">{formatIDR(b.amount)}</p>
              <Button variant="ghost" size="icon" onClick={() => { setConfirmId(b.id); setConfirmOpen(true); }} data-testid={`bill-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          );
        })}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Apakah Anda yakin ingin menghapus tagihan ini?
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button variant="destructive" className="bg-loss text-white hover:bg-loss/90" onClick={handleConfirmDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
